//@author travis
import { DataAccess } from "../dataaccess/data.access";
import { Cache, Invalidate } from "../decorators/cache.decorator";
import { Proto } from "../pojo/Proto";
import { ExtendUtil } from "../util/extend-util";
import { Item } from "../pojo/Item";

interface AbstractFavoriteItem {
    itemId: number;
    itemTypeId: number;
}

export type FavoriteItem = AbstractFavoriteItem & ({ isFav: boolean } | { favorite: Proto.CharBoolean });
export namespace FavoriteItem {
    export function isStarred(item: FavoriteItem): boolean {
        return "isFav" in item ? !!item.isFav : item.favorite === "T";
    }
}

export class FavoriteService {
    private static TYPEID_MAP: any = {
        //favorite-events
        11: 1,
        //favorite-spaces
        14: 4,
        //favorite-resources
        16: 6,
        //favorite-event-searches
        101: 11,
        //favorite-organization-searches
        102: 16,
        //favorite-space-searches
        104: 14,
        //favorite-resource-searches
        106: 15,
        //favorite-task-searches
        110: 10,
        //favorite-reports
        1000: 9,
    };

    private static composeUrl(item: { itemId: number; itemTypeId: Item.Id }, caller: string) {
        return DataAccess.injectCaller(
            "/favorites.json?object_type=" +
                (FavoriteService.TYPEID_MAP[item.itemTypeId] || item.itemTypeId) +
                "&object_id=" +
                item.itemId,
            caller,
        );
    }

    @Invalidate({
        serviceName: "FavoriteService",
        methodName: "isFavorite",
        objectFunc: (args) => [FavoriteService.TYPEID_MAP[args[0].itemTypeId] || args[0].itemTypeId, args[0].itemId],
    })
    @Invalidate({
        startsWith: "SpaceService",
        patternFunc: (args) => args[0].itemId,
        validate: (args) => args[0].itemTypeId === 4,
    })
    @Invalidate({
        serviceName: "SpaceService",
        methodName: "getSpacesBySearchQuery",
        validate: (args) => args[0].itemTypeId === 4,
    })
    @Invalidate({
        startsWith: "ResourceService",
        patternFunc: (args) => args[0].itemId,
        validate: (args) => args[0].itemTypeId === 6,
    })
    @Invalidate({
        serviceName: "ResourceService",
        methodName: "getResourcesBySearchQuery",
        validate: (args) => args[0].itemTypeId === 6,
    })
    public static add(item: { itemId: number; itemTypeId: Item.Id }) {
        return DataAccess.put(FavoriteService.composeUrl(item, "FavoriteService.add"));
    }

    @Invalidate({
        serviceName: "FavoriteService",
        methodName: "isFavorite",
        objectFunc: (args) => [FavoriteService.TYPEID_MAP[args[0].itemTypeId] || args[0].itemTypeId, args[0].itemId],
    })
    @Invalidate({
        startsWith: "SpaceService",
        patternFunc: (args) => args[0].itemId,
        validate: (args) => args[0].itemTypeId === 4,
    })
    @Invalidate({
        serviceName: "SpaceService",
        methodName: "getSpacesBySearchQuery",
        validate: (args) => args[0].itemTypeId === 4,
    })
    @Invalidate({
        startsWith: "ResourceService",
        patternFunc: (args) => args[0].itemId,
        validate: (args) => args[0].itemTypeId === 6,
    })
    @Invalidate({
        serviceName: "ResourceService",
        methodName: "getResourcesBySearchQuery",
        validate: (args) => args[0].itemTypeId === 6,
    })
    public static remove(item: { itemId: number; itemTypeId: Item.Id }) {
        return DataAccess.delete(FavoriteService.composeUrl(item, "FavoriteService.remove"));
    }

    public static async toggle(item: FavoriteItem) {
        const result = FavoriteItem.isStarred(item)
            ? await FavoriteService.remove(item)
            : await FavoriteService.add(item);
        ExtendUtil.extend(item.itemId, item.itemTypeId, {
            favorite: "favorite" in item ? item.favorite : "F",
            isFav: "isFav" in item ? item.isFav : false,
        });
        return result;
    }

    @Cache({ immutable: true, targetName: "FavoriteService" })
    public static isFavorite({ itemTypeId, itemId }: { itemTypeId: number; itemId: number }): Promise<{
        isFavorite: { isFavorite: 0 | 1 };
    }> {
        return DataAccess.get(
            DataAccess.injectCaller(
                "/favorites/isfavorite.json?itemTypeId=" + itemTypeId + "&itemId=" + itemId,
                "FavoriteService.isFavorite",
            ),
        );
    }
}
